/**
 * Workspace SSO for Simplii Brain embeds and direct ?simplii_token= links.
 * JWT is never stored in Element's mx_* slots — only in sessionStorage (simplii_matrix_jwt).
 */

import { resetSimpliiManagedRecoveryState } from "./simpliiManagedRecovery";
import { saveSimpliiPlatformRole } from "../auth/slackImportAccess";

export const SIMPLII_IFRAME_AUTH_TYPE = "SIMPLII_IFRAME_AUTH" as const;
export const SIMPLII_MATRIX_SSO_REQUEST = "SIMPLII_MATRIX_SSO_REQUEST" as const;
export const SIMPLII_MATRIX_SSO_DONE = "SIMPLII_MATRIX_SSO_DONE" as const;
export const SIMPLII_MATRIX_CREDENTIALS = "SIMPLII_MATRIX_CREDENTIALS" as const;

const TOKEN_PARAM = "simplii_token";
const JWT_KEY = "simplii_matrix_jwt";
const MATRIX_CREDENTIAL_KEY = "simplii.matrix.credentials.applied";

declare global {
    interface Window {
        __simpliiWorkspaceSsoBootstrapInstalled?: boolean;
    }
}

let simpliiMatrixLoginInFlight = false;
let simpliiLastMatrixCredentialKey = "";

function queryParams(): URLSearchParams {
    try {
        return new URLSearchParams(window.location.search);
    } catch {
        return new URLSearchParams();
    }
}

function readJwtFromUrl(): string | null {
    const t = queryParams().get(TOKEN_PARAM)?.trim();
    return t && (t.match(/\./g) || []).length >= 2 ? t : null;
}

function readJwt(): string | null {
    const fromUrl = readJwtFromUrl();
    if (fromUrl) return fromUrl;
    try {
        const stored = sessionStorage.getItem(JWT_KEY)?.trim();
        if (stored && (stored.match(/\./g) || []).length >= 2) return stored;
    } catch {
        /* ignore */
    }
    return null;
}

function persistJwt(jwt: string): void {
    try {
        sessionStorage.setItem(JWT_KEY, jwt);
    } catch {
        /* ignore */
    }
}

function configuredParentOrigin(): string | null {
    try {
        const configured = queryParams().get("parentOrigin")?.trim();
        if (configured) return new URL(configured).origin;
    } catch {
        /* ignore */
    }
    return null;
}

function parentOrigin(): string {
    return configuredParentOrigin() ?? "*";
}

function isTrustedParentMessage(ev: MessageEvent): boolean {
    if (window.parent === window || ev.source !== window.parent) {
        return false;
    }

    const expectedOrigin = configuredParentOrigin();
    return Boolean(expectedOrigin && ev.origin === expectedOrigin);
}

function postEmbedReady(): void {
    if (window.parent === window) return;
    try {
        window.parent.postMessage({ type: "simplii:embed-ready" }, parentOrigin());
    } catch {
        /* ignore */
    }
}

function hasMatrixSession(): boolean {
    try {
        if (localStorage.getItem("mx_access_token")) return true;
        for (let i = 0; i < localStorage.length; i++) {
            const k = localStorage.key(i);
            if (!k) continue;
            if (k.startsWith("mx_") && k.includes("accessToken")) return true;
            if (k.startsWith("mx_") && k.includes("access_token")) return true;
        }
    } catch {
        /* ignore */
    }
    return false;
}

function clearPoisonedPlannerTokens(): void {
    if (hasMatrixSession()) return;
    try {
        for (const store of [localStorage, sessionStorage]) {
            const jwt = sessionStorage.getItem(JWT_KEY);
            const legacy = store.getItem("access_token");
            if (!legacy) continue;
            if (jwt && legacy === jwt) {
                store.removeItem("access_token");
                continue;
            }
            if ((legacy.match(/\./g) || []).length >= 2) {
                store.removeItem("access_token");
            }
        }
    } catch {
        /* ignore */
    }
}

function homeserverBase(): string {
    const fromConfig = (window as unknown as { __simpliiHsUrl?: string }).__simpliiHsUrl;
    if (fromConfig?.trim()) return fromConfig.replace(/\/+$/, "");
    return location.origin.replace(/\/+$/, "");
}

function finishLoginToken(loginToken: string): void {
    const p = queryParams();
    p.set("loginToken", loginToken);
    location.replace(`${location.pathname}?${p.toString()}${location.hash}`);
}

function storeMatrixCredentials(data: Record<string, unknown>): void {
    const role = typeof data.role === "string" ? data.role : "";

    saveSimpliiPlatformRole(role);

    const accessToken =
        (typeof data.accessToken === "string" && data.accessToken.trim()) ||
        (typeof data.access_token === "string" && data.access_token.trim()) ||
        "";
    if (!accessToken) return;

    const homeserverUrl =
        (typeof data.homeserverUrl === "string" && data.homeserverUrl) ||
        (typeof data.baseUrl === "string" && data.baseUrl) ||
        homeserverBase();

    const userId =
        (typeof data.userId === "string" && data.userId) || (typeof data.user_id === "string" && data.user_id) || "";

    const deviceId =
        (typeof data.deviceId === "string" && data.deviceId) ||
        (typeof data.device_id === "string" && data.device_id) ||
        "";

        const credentialKey = [userId, homeserverUrl, deviceId].join(":");

    try {
        // const previousCredentialKey = sessionStorage.getItem(MATRIX_CREDENTIAL_KEY);

        // if (
        //   simpliiMatrixLoginInFlight ||
        //   simpliiLastMatrixCredentialKey === credentialKey ||
        //   previousCredentialKey === credentialKey
        // ) {
        //   postEmbedReady();
        //   return;
        // }

        const previousCredentialKey = sessionStorage.getItem(MATRIX_CREDENTIAL_KEY);
const currentUserId = localStorage.getItem("mx_user_id") || "";
const currentDeviceId = localStorage.getItem("mx_device_id") || "";
const hasSession = hasMatrixSession();

const sameMatrixSession =
    currentUserId === userId &&
    currentDeviceId === deviceId;

const matrixSessionChanged =
    (Boolean(currentUserId) && Boolean(userId) && currentUserId !== userId) ||
    (Boolean(currentDeviceId) && Boolean(deviceId) && currentDeviceId !== deviceId);

        // If Brain user changed, clear old Element Matrix session before applying new one.
        if (matrixSessionChanged) {
            for (const key of Object.keys(localStorage)) {
                if (
                    key.startsWith("mx_") ||
                    key.startsWith("matrix_") ||
                    key.startsWith("crypto.") ||
                    key.startsWith("mxc_")
                ) {
                    localStorage.removeItem(key);
                }
            }

            sessionStorage.removeItem(MATRIX_CREDENTIAL_KEY);
            simpliiLastMatrixCredentialKey = "";
        }

        // Only skip duplicate if Element still has a valid Matrix session for same user.
        // If user clicked "Remove from this device", localStorage is cleared, so re-apply.
        if (
            hasSession &&
            sameMatrixSession &&
            (
                simpliiMatrixLoginInFlight ||
                simpliiLastMatrixCredentialKey === credentialKey ||
                previousCredentialKey === credentialKey
            )
        ) {
            postEmbedReady();
            return;
        }

        simpliiMatrixLoginInFlight = true;
        simpliiLastMatrixCredentialKey = credentialKey;
        sessionStorage.setItem(MATRIX_CREDENTIAL_KEY, credentialKey);

        localStorage.setItem("mx_access_token", accessToken);
        if (userId) localStorage.setItem("mx_user_id", userId);
        if (deviceId) localStorage.setItem("mx_device_id", deviceId);
        localStorage.setItem("mx_hs_url", homeserverUrl);

        resetSimpliiManagedRecoveryState();

        postEmbedReady();
        location.reload();
    } catch {
        simpliiMatrixLoginInFlight = false;
    }
}

function onMessage(ev: MessageEvent): void {
    const data = ev.data as Record<string, unknown> | null;
    if (!data || typeof data !== "object") return;

    if (data.type === SIMPLII_IFRAME_AUTH_TYPE && typeof data.token === "string") {
        if (!isTrustedParentMessage(ev)) return;

        // The Brain parent performs the workspace-JWT → Matrix credential exchange.
        // Keep the token only as a short-lived hint; do not start browser OIDC from
        // inside the iframe because Brain login pages cannot safely complete there.
        persistJwt(String(data.token));
        return;
    }

    if (data.type === SIMPLII_MATRIX_CREDENTIALS) {
        if (!isTrustedParentMessage(ev)) return;

        console.info(
            "[Simplii] Workspace bootstrap received Matrix credentials",
            {
                origin: ev.origin,
    
                userId:
                    typeof data.userId === "string"
                        ? data.userId
                        : undefined,
    
                deviceId:
                    typeof data.deviceId === "string"
                        ? data.deviceId
                        : undefined,
    
                homeserverUrl:
                    typeof data.homeserverUrl === "string"
                        ? data.homeserverUrl
                        : undefined,
            },
        );
    
        storeMatrixCredentials(data);
        return;
    }

    if (data.type === SIMPLII_MATRIX_SSO_DONE && typeof data.loginToken === "string") {
        finishLoginToken(String(data.loginToken));
    }
}

function boot(): void {
    clearPoisonedPlannerTokens();

    const loginToken = queryParams().get("loginToken");
    if (loginToken && window.opener) {
        try {
            window.opener.postMessage({ type: SIMPLII_MATRIX_SSO_DONE, loginToken }, "*");
        } catch {
            /* ignore */
        }
        window.setTimeout(() => {
            try {
                window.close();
            } catch {
                /* ignore */
            }
        }, 400);
        return;
    }

    if (loginToken) return;
    if (hasMatrixSession()) {
        postEmbedReady();
        return;
    }

    const jwt = readJwt();
    if (jwt) persistJwt(jwt);
}

/** Call before Element React shell loads. */
export function startWorkspaceSsoBootstrap(): void {
    if (
        typeof window === "undefined" ||
        window.__simpliiWorkspaceSsoBootstrapInstalled
    ) {
        return;
    }

    window.__simpliiWorkspaceSsoBootstrapInstalled = true;

    console.info(
        "[Simplii] Workspace SSO bootstrap installed",
    );

    window.addEventListener(
        "message",
        onMessage,
    );

    boot();
}